import PageLayout from "@/components/feature/PageLayout";
import PageCTA from "@/components/feature/PageCTA";
import Breadcrumb from "@/components/feature/Breadcrumb";
import AnimatedSection from "@/components/AnimatedSection";
import { caseStudies, portfolioProjects } from "@/mocks/portfolioPageData";

export default function CaseStudyPage({ slug }: { slug: string }) {
  const study = caseStudies.find((c) => c.slug === slug);

  if (!study) return null;

  const relatedProjects = portfolioProjects.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <PageLayout>
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "Portfolio", href: "/portfolio" },
          { label: study.title },
        ]}
      />

      {/* Project Hero */}
      <section className="relative min-h-[440px] md:min-h-[500px] flex items-end overflow-hidden bg-hero-gradient">
        <div className="absolute inset-0">
          <img
            src={study.heroImage}
            alt={study.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/70 to-primary-950/30" />
        </div>

        <div className="container-astro relative z-10 pt-28 md:pt-32 pb-14 md:pb-16">
          <AnimatedSection>
            <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-accent-500/15 border border-accent-500/30 mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-accent-400 animate-pulse-dot" />
              <span className="text-xs font-semibold text-accent-300 tracking-wide uppercase">
                {study.badge}
              </span>
            </span>
          </AnimatedSection>
          <AnimatedSection delay={100}>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[52px] font-extrabold text-background-50 leading-[1.1] mb-4 font-heading tracking-tight max-w-3xl">
              {study.title}
            </h1>
          </AnimatedSection>
          <AnimatedSection delay={200}>
            <div className="flex flex-wrap gap-2">
              <span className="px-3 py-1.5 rounded-lg bg-background-50/10 backdrop-blur-sm border border-background-50/20 text-background-200 text-xs font-medium">
                Industry: {study.industry}
              </span>
              {study.techStack.slice(0, 4).map((t) => (
                <span
                  key={t.name}
                  className="px-3 py-1.5 rounded-lg bg-background-50/10 backdrop-blur-sm border border-background-50/20 text-background-200 text-xs font-medium"
                >
                  {t.name}
                </span>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Overview + Challenge */}
      <section className="py-20 md:py-28 bg-background-50">
        <div className="container-astro">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
            <AnimatedSection>
              <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-accent-500/10 border border-accent-500/20 mb-5">
                <span className="w-1.5 h-1.5 rounded-full bg-accent-500" />
                <span className="text-xs font-semibold text-accent-600 tracking-wide uppercase">
                  Project Overview
                </span>
              </span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                Overview
              </h2>
              <p className="text-base text-foreground-600 leading-relaxed">
                {study.overview}
              </p>
            </AnimatedSection>

            <AnimatedSection delay={150}>
              <div className="p-7 md:p-8 rounded-2xl bg-background-100 border border-background-200/60">
                <span className="inline-flex items-center gap-2 mb-4">
                  <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-50 text-accent-600">
                    <i className="ri-error-warning-line text-lg" />
                  </span>
                  <h3 className="text-lg font-bold text-foreground-950 font-heading">
                    Business Challenge
                  </h3>
                </span>
                <p className="text-sm text-foreground-600 leading-relaxed mb-5">
                  {study.challenge}
                </p>
                <h4 className="text-sm font-bold text-foreground-800 uppercase tracking-wider mb-3">
                  Goals
                </h4>
                <ul className="space-y-2.5">
                  {study.goals.map((goal) => (
                    <li key={goal} className="flex items-start gap-3">
                      <span className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-50 text-accent-600 shrink-0 mt-0.5">
                        <i className="ri-check-line text-xs" />
                      </span>
                      <span className="text-sm text-foreground-600">{goal}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Our Solution */}
      <section className="py-20 md:py-28 bg-background-100 relative overflow-hidden">
        <div className="absolute inset-0 dot-pattern opacity-30" />
        <div className="container-astro relative z-10">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                Our Solution
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <AnimatedSection>
            <div className="max-w-3xl mx-auto p-7 md:p-9 rounded-2xl bg-background-50 border border-background-200/60">
              <p className="text-base text-foreground-600 leading-relaxed text-center">
                {study.solution}
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Architecture */}
      <section className="py-20 md:py-28 bg-background-50">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                Solution Architecture
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="max-w-3xl mx-auto">
            <div className="flex flex-col items-center gap-3">
              {study.architecture.map((layer, i) => (
                <AnimatedSection key={layer.name} delay={i * 120}>
                  <div className="relative">
                    {i > 0 && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-px h-4 bg-gradient-to-b from-transparent to-accent-500/40" />
                    )}
                    <div className="flex items-center gap-4 px-6 py-4 rounded-xl bg-background-100 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-300 w-[280px] md:w-[400px]">
                      <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-50 text-accent-600">
                        <i className={`${layer.icon} text-lg`} />
                      </span>
                      <div>
                        <h4 className="text-sm font-bold text-foreground-950 font-heading">
                          {layer.name}
                        </h4>
                        <p className="text-xs text-foreground-500">{layer.desc}</p>
                      </div>
                    </div>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-20 md:py-28 bg-background-100">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                Key Features
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {study.keyFeatures.map((feature, i) => (
              <AnimatedSection key={feature.title} delay={i * 100}>
                <div className="group p-6 md:p-7 rounded-2xl bg-background-50 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-400 card-lift h-full">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-50 text-accent-600 mb-5 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-300">
                    <span className="w-6 h-6 flex items-center justify-center">
                      <i className={`${feature.icon} text-xl`} />
                    </span>
                  </div>
                  <h3 className="text-base font-bold text-foreground-950 mb-2 font-heading">{feature.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{feature.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Stack + Process */}
      <section className="py-20 md:py-28 bg-background-50">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                Technology Stack
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
            {study.techStack.map((tech, i) => (
              <AnimatedSection key={tech.name} delay={i * 60}>
                <div className="flex flex-col items-center gap-3 p-5 md:p-6 rounded-2xl bg-background-100 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-300 card-lift h-full">
                  {tech.logo ? (
                    <img src={tech.logo} alt={tech.name} className="w-10 h-10 object-contain" />
                  ) : (
                    <span className="w-10 h-10 flex items-center justify-center text-accent-500">
                      <i className={`${tech.icon} text-2xl`} />
                    </span>
                  )}
                  <span className="text-sm font-semibold text-foreground-800 text-center">{tech.name}</span>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Development Process */}
      <section className="py-20 md:py-28 bg-background-100">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                Development Process
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="relative max-w-3xl mx-auto">
            <div className="absolute left-[25px] top-0 bottom-0 w-px bg-gradient-to-b from-accent-500/30 via-accent-500/50 to-accent-500/30" />
            <div className="flex flex-col gap-7">
              {study.process.map((step, i) => (
                <AnimatedSection key={step.title} delay={i * 100}>
                  <div className="flex items-start gap-5 group">
                    <div className="relative z-10 flex items-center justify-center w-[52px] h-[52px] rounded-full bg-background-100 border-2 border-accent-500/30 text-accent-600 font-extrabold text-sm font-heading shrink-0 group-hover:bg-accent-500 group-hover:text-background-50 group-hover:border-accent-500 transition-all duration-300">
                      {i + 1}
                    </div>
                    <div className="pt-2.5">
                      <h3 className="text-base font-bold text-foreground-950 mb-1.5 font-heading">
                        {step.title}
                      </h3>
                      <p className="text-sm text-foreground-500 leading-relaxed">{step.desc}</p>
                    </div>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-20 md:py-28 bg-background-50 relative overflow-hidden">
        <div className="absolute inset-0 dot-pattern opacity-30" />
        <div className="container-astro relative z-10">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                Outcomes & Results
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 md:gap-6">
            {study.results.map((result, i) => (
              <AnimatedSection key={result.title} delay={i * 100}>
                <div className="group p-6 md:p-7 rounded-2xl bg-background-100 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-400 card-lift h-full">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-50 text-accent-600 mb-5 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-300">
                    <span className="w-6 h-6 flex items-center justify-center">
                      <i className={`${result.icon} text-xl`} />
                    </span>
                  </div>
                  <h3 className="text-base font-bold text-foreground-950 mb-2 font-heading">{result.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{result.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Screenshots */}
      <section className="py-20 md:py-28 bg-background-100">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                Product Screenshots
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {study.screenshots.map((shot, i) => (
              <AnimatedSection key={i} delay={i * 100}>
                <div className="group rounded-2xl overflow-hidden border border-background-200/60 hover:border-accent-300/40 hover:shadow-xl hover:shadow-accent-500/10 transition-all duration-500 card-lift">
                  <div className="img-scale-hover overflow-hidden">
                    <img
                      src={shot}
                      alt={`${study.title} screenshot ${i + 1}`}
                      className="w-full h-[240px] object-cover object-top"
                    />
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Related Projects */}
      <section className="py-20 md:py-28 bg-background-50">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                Related Projects
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7">
            {relatedProjects.map((project, i) => (
              <AnimatedSection key={project.slug} delay={i * 100}>
                <a
                  href={`/portfolio/${project.slug}`}
                  className="group block rounded-2xl overflow-hidden border border-background-200/60 hover:border-accent-300/40 hover:shadow-xl hover:shadow-accent-500/10 transition-all duration-500 card-lift bg-background-100 h-full flex flex-col"
                >
                  <div className="relative overflow-hidden h-[180px]">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                  </div>
                  <div className="p-5 flex flex-col flex-grow">
                    <p className="text-xs font-semibold text-accent-600 uppercase tracking-wider mb-1.5">
                      {project.industry}
                    </p>
                    <h3 className="text-base font-bold text-foreground-950 mb-3 font-heading">
                      {project.title}
                    </h3>
                    <span className="mt-auto inline-flex items-center gap-1.5 text-sm font-semibold text-accent-600 group-hover:text-accent-700 transition-colors btn-arrow-hover">
                      View Case Study
                      <i className="ri-arrow-right-line btn-arrow text-xs transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </div>
                </a>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <PageCTA
        heading="Have a project in mind?"
        text="Let's build something that scales."
        primaryCTA="Start Your Project"
        secondaryCTA="Talk to a Laravel Expert"
      />
    </PageLayout>
  );
}